import {createScheduleSummary} from './schedule.js';
import {createSessionScheduleController} from './session-schedule-controller.js';
import {validatePlannedJournal,recoverableJournalError} from './validation.js';
import {JournalAnalyticsEvent} from './analytics.js';
import {prepareLiveStart} from './live-start.js';
import {createLiveDashboardModel} from '../live/dashboard-model.js';
import {journalPageMarkup} from './page-view.js';

function code(error,fallback='JOURNAL_ACTION_FAILED'){
  return String(error?.code||error?.message||fallback).slice(0,80);
}

function timezone(){
  try{return Intl.DateTimeFormat().resolvedOptions().timeZone||undefined;}
  catch{return undefined;}
}

function liveStageControlBlocked(conflicts=[],sessionId){
  const id=String(sessionId||'');
  if(!id)return false;
  return (conflicts||[]).some(row=>
    (row?.entityType==='smoking_session'&&String(row?.id||'')===id)
    ||(String(row?.sessionId||'')===id&&row?.conflict?.reason==='parent_session_conflict')
  );
}

export function createJournalPageController({
  runtime,
  analytics=null,
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!runtime?.sessions||!runtime?.sessionBundle||!runtime?.sessionControllers||!runtime?.referenceData){
    throw new TypeError('Journal runtime is required');
  }

  let state=Object.freeze({kind:'loading'});
  let editors=null;
  let refs=null;
  let liveTimelineLimit=60;
  let liveTimelineSessionId=null;

  function track(name,properties={}){
    try{analytics?.track?.(name,properties);}catch{}
  }

  function setState(next){
    state=Object.freeze({...next});
    return state;
  }

  async function localStatus(){
    const [pending,conflicts]=await Promise.all([
      runtime.offline.pendingSummary(),
      runtime.offline.conflicts()
    ]);
    return Object.freeze({
      pendingCount:Object.values(pending).reduce((sum,value)=>sum+Number(value||0),0),
      conflicts
    });
  }

  async function listState({notice='',noticeKind='neutral'}={}){
    const [sessions,status]=await Promise.all([
      runtime.sessions.list(),
      localStatus()
    ]);
    return setState({
      kind:'list',
      sessions,
      pendingCount:status.pendingCount,
      conflicts:status.conflicts,
      notice,
      noticeKind
    });
  }

  async function refresh(){
    try{return await listState();}
    catch(error){
      return setState({kind:'error',errorCode:code(error,'JOURNAL_LOAD_FAILED')});
    }
  }

  async function recoverActiveSession(){
    try{
      if(!runtime.liveRecovery?.recover)return refresh();
      const recovery=await runtime.liveRecovery.recover();
      if(recovery.status!=='active')return listState();

      liveTimelineSessionId=null;
      liveTimelineLimit=60;
      editors=await runtime.sessionControllers(recovery.sessionId);
      const notificationWarning=recovery.notifications?.status==='failed'
        ?' Aktywne wędzenie zostało wznowione, ale przypomnienia wymagają ponownego uzgodnienia.'
        :'';
      return buildEditorState({
        notice:'Wznowiono aktywne wędzenie po ponownym uruchomieniu aplikacji.'+notificationWarning,
        noticeKind:recovery.notifications?.status==='failed'?'warning':'success'
      });
    }catch(error){
      if(String(error?.message||'')==='MULTIPLE_ACTIVE_SMOKING_SESSIONS'){
        const list=await listState({
          notice:'Wykryto więcej niż jedno aktywne wędzenie. Dymistrz nie wybiera automatycznie procesu, aby nie nadpisać danych.',
          noticeKind:'error'
        });
        return setState({
          ...list,
          recoveryStatus:'multiple_active_sessions'
        });
      }
      return setState({kind:'error',errorCode:code(error,'LIVE_RECOVERY_FAILED')});
    }
  }

  async function buildEditorState({notice='',noticeKind='neutral'}={}){
    if(!editors)throw new Error('JOURNAL_EDITOR_NOT_OPEN');

    const session=editors.sessionEditor.snapshot().session;
    if(!session)throw new Error('SMOKING_SESSION_NOT_OPEN');

    editors.itemEditor.setSessionStatus(session.status);
    editors.woodEditor.setSessionStatus(session.status);
    editors.stageEditor.setSessionStatus(session.status);

    const itemState=await editors.itemEditor.refresh();
    editors.stageEditor.setItems(itemState.items);
    const [woodState,stageState,bundle,status,liveNotificationPreferences]=await Promise.all([
      editors.woodEditor.refresh(),
      editors.stageEditor.refresh(),
      runtime.sessionBundle(session.id),
      localStatus(),
      runtime.liveNotifications?.getPreferences?.()||Promise.resolve(null)
    ]);

    refs=await runtime.referenceData({locale:'pl'});
    const latest=editors.sessionEditor.snapshot().session;
    const schedule=createScheduleSummary({
      plannedStartAt:latest.plannedStartAt,
      plannedEndAt:latest.plannedEndAt,
      stageSummaries:stageState.summaries
    });

    if(latest.status==='smoking'){
      if(liveTimelineSessionId!==latest.id){
        liveTimelineSessionId=latest.id;
        liveTimelineLimit=60;
      }
    }

    return setState({
      kind:'editor',
      notice,
      noticeKind,
      model:Object.freeze({
        session:latest,
        itemState,
        woodState,
        stageState,
        preparations:bundle?.preparations||[],
        itemNotes:bundle?.itemNotes||[],
        sessionNote:bundle?.sessionNote||null,
        outcome:bundle?.outcome||null,
        photos:bundle?.photos||[],
        liveDashboard:latest.status==='smoking'
          ?createLiveDashboardModel({
            bundle:{...(bundle||{}),session:latest},
            now:Date.now,
            timelineLimit:liveTimelineLimit,
            stageControlBlocked:liveStageControlBlocked(status.conflicts,latest.id)
          })
          :null,
        liveNotificationPreferences,
        refs,
        schedule,
        pendingCount:status.pendingCount,
        conflicts:status.conflicts,
        offline:!isOnline(),
        mediaAvailable:Boolean(runtime.photoFlow),
        timeZone:timezone()
      })
    });
  }

  async function open(id){
    try{
      liveTimelineSessionId=null;
      liveTimelineLimit=60;
      editors=await runtime.sessionControllers(id);
      return await buildEditorState();
    }catch(error){
      return setState({kind:'error',errorCode:code(error,'JOURNAL_OPEN_FAILED')});
    }
  }

  async function createDraft({title=null}={}){
    try{
      const saved=await runtime.sessions.createDraft({title});
      track(JournalAnalyticsEvent.SESSION_CREATE_STARTED,{offline:!isOnline()});
      editors=await runtime.sessionControllers(saved.id);
      return await buildEditorState({notice:'Szkic został zapisany na tym urządzeniu.',noticeKind:'success'});
    }catch(error){
      return setState({kind:'error',errorCode:code(error,'JOURNAL_CREATE_FAILED')});
    }
  }

  async function resumeDraft(){
    const draft=await runtime.sessions.resumeDraft();
    return draft?open(draft.id):createDraft({});
  }

  async function action(work,{
    success='',
    event=null,
    eventProperties={},
    observabilityAction=null,
    observabilitySessionId=null
  }={}){
    try{
      const result=await work();
      if(event)track(event,{...eventProperties,offline:!isOnline()});
      if(observabilityAction){
        runtime.liveObservability?.recordAction?.({
          sessionId:observabilitySessionId,
          action:observabilityAction,
          outcome:'success'
        });
      }
      return await buildEditorState({notice:success,noticeKind:success?'success':'neutral'});
    }catch(error){
      if(observabilityAction){
        runtime.liveObservability?.recordAction?.({
          sessionId:observabilitySessionId,
          action:observabilityAction,
          outcome:'failure',
          errorCode:code(error)
        });
      }
      const current=editors?.sessionEditor?.snapshot?.();
      const recovered=recoverableJournalError(error,{draft:current?.session||null});
      return await buildEditorState({
        notice:recovered.message+' Kod: '+recovered.errorCode,
        noticeKind:'error'
      });
    }
  }

  async function saveTitle(title){
    return action(
      ()=>editors.sessionEditor.autosave({title}),
      {success:'Nazwa została zapisana.',event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'title'}}
    );
  }

  async function saveSchedule(formData,{utcOffsetMinutes=null,timeZone=null}={}){
    const controller=createSessionScheduleController({
      sessionEditor:editors.sessionEditor,
      getStageSummaries:()=>editors.stageEditor.snapshot().summaries
    });
    return action(
      ()=>controller.save(formData,{utcOffsetMinutes,timeZone}),
      {success:'Termin został zapisany.',event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'schedule'}}
    );
  }

  async function setEquipment(smokerId){
    return action(async()=>{
      const id=String(smokerId||'').trim();
      if(!id)return editors.sessionEditor.clearSmoker();
      const smoker=(refs?.smokers||[]).find(row=>String(row.id)===id);
      const capabilities=(refs?.capabilities||[]).find(row=>String(row.smokerId||row.id)===id)||{
        smokerId:id,
        fuelTypes:[],
        minTemperatureCelsius:null,
        maxTemperatureCelsius:null,
        coldSmoking:'unknown',
        coldSmokeMaxCelsius:null,
        temperatureControl:'unknown',
        controlPrecisionCelsius:null
      };
      if(!smoker)throw new Error('SMOKER_NOT_FOUND');
      return editors.sessionEditor.setSmoker({smoker,capabilities});
    },{
      success:'Sprzęt zapisany w planie.',
      event:JournalAnalyticsEvent.SESSION_EDIT,
      eventProperties:{field:'equipment'}
    });
  }

  async function createCustomProduct(input){
    return action(
      ()=>runtime.customProducts.create(input),
      {success:'Własny produkt został dodany.',event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'custom_product'}}
    );
  }

  async function beginItemEdit(id){
    await editors.itemEditor.beginEdit(id);
    return buildEditorState();
  }
  function cancelItemEdit(){
    editors.itemEditor.beginCreate();
    return buildEditorState();
  }
  async function saveItem(input){
    const creating=!editors.itemEditor.snapshot().editingId;
    return action(
      ()=>editors.itemEditor.save(input),
      {
        success:'Produkt zapisany.',
        event:creating?JournalAnalyticsEvent.ITEM_ADDED:JournalAnalyticsEvent.SESSION_EDIT,
        eventProperties:{field:'item'}
      }
    );
  }
  async function moveItem(id,direction){
    return action(()=>editors.itemEditor.move(id,direction),{
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'item_order'}
    });
  }
  async function removeItem(id){
    return action(()=>editors.itemEditor.remove(id),{
      success:'Produkt usunięty z planu.',
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'item_remove'}
    });
  }

  async function savePreparation(itemId,input,{itemNote=''}={}){
    const session=editors.sessionEditor.snapshot().session;
    return action(async()=>{
      const saved=await runtime.preparations.save(itemId,input,{sessionStatus:session.status});
      const note=String(itemNote||'').trim();
      const current=await runtime.notes.getItem(itemId);
      if(note){
        await runtime.notes.saveItem(itemId,note,{sessionStatus:session.status});
      }else if(current){
        await runtime.notes.clearItem(itemId,{sessionStatus:session.status});
      }
      return saved;
    },{
      success:'Przygotowanie i notatka produktu zapisane.',
      event:JournalAnalyticsEvent.SESSION_EDIT,
      eventProperties:{field:'preparation'}
    });
  }

  async function beginWoodEdit(id){
    await editors.woodEditor.beginEdit(id);
    return buildEditorState();
  }
  function cancelWoodEdit(){
    editors.woodEditor.beginCreate();
    return buildEditorState();
  }
  async function saveWood(input){
    return action(()=>editors.woodEditor.save(input),{
      success:'Drewno zapisane w planie.',
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'wood'}
    });
  }
  async function moveWood(id,direction){
    return action(()=>editors.woodEditor.move(id,direction),{
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'wood_order'}
    });
  }
  async function removeWood(id){
    return action(()=>editors.woodEditor.remove(id),{
      success:'Drewno usunięte z planu.',
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'wood_remove'}
    });
  }

  async function beginStageEdit(id){
    await editors.stageEditor.beginEdit(id);
    return buildEditorState();
  }
  function cancelStageEdit(){
    editors.stageEditor.beginCreate();
    return buildEditorState();
  }
  async function saveStage(input){
    const creating=!editors.stageEditor.snapshot().editingId;
    return action(()=>editors.stageEditor.save(input),{
      success:'Etap zapisany.',
      event:creating?JournalAnalyticsEvent.STAGE_ADDED:JournalAnalyticsEvent.SESSION_EDIT,
      eventProperties:{field:'stage'}
    });
  }
  async function moveStage(id,direction){
    return action(()=>editors.stageEditor.move(id,direction),{
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'stage_order'}
    });
  }
  async function removeStage(id,{confirmed=false}={}){
    if(!confirmed)return editors.stageEditor.requestRemove(id);
    return action(()=>editors.stageEditor.remove(id,{confirmed:true}),{
      success:'Etap usunięty.',
      event:JournalAnalyticsEvent.SESSION_EDIT,eventProperties:{field:'stage_remove'}
    });
  }

  async function saveSessionNote(text){
    const session=editors.sessionEditor.snapshot().session;
    if(!String(text||'').trim())return clearSessionNote();
    return action(
      ()=>runtime.notes.saveSession(session.id,text,{sessionStatus:session.status}),
      {success:'Notatka zapisana.'}
    );
  }

  async function clearSessionNote(){
    const session=editors.sessionEditor.snapshot().session;
    return action(
      ()=>runtime.notes.clearSession(session.id,{sessionStatus:session.status}),
      {success:'Notatka wyczyszczona.'}
    );
  }

  async function attachPhoto(input,{file}={}){
    if(!runtime.photoFlow){
      return buildEditorState({
        notice:'Wysyłanie zdjęć pozostaje wyłączone do czasu uruchomienia bezpiecznej synchronizacji 0.3.',
        noticeKind:'warning'
      });
    }
    return action(()=>runtime.photoFlow.attach(input,{file}),{success:'Zdjęcie zapisane.'});
  }

  async function removePhoto(id){
    if(!runtime.photoFlow)throw new Error('JOURNAL_MEDIA_UNAVAILABLE');
    return action(()=>runtime.photoFlow.remove(id),{success:'Zdjęcie usunięte.'});
  }

  async function saveAsPlanned(){
    const session=editors.sessionEditor.snapshot().session;
    const bundle=await runtime.sessionBundle(session.id);
    const validation=validatePlannedJournal({
      items:bundle.items,
      stages:bundle.stages,
      stageTargets:bundle.stageTargets
    });
    if(!validation.valid){
      track(JournalAnalyticsEvent.VALIDATION_ERROR,{
        error_count:validation.errorCount,
        status:session.status
      });
      return buildEditorState({
        notice:'Uzupełnij plan przed zapisaniem jako Zaplanowane: '+validation.errors.map(error=>error.message).join(' '),
        noticeKind:'error'
      });
    }
    return action(()=>editors.sessionEditor.saveAsPlanned(),{
      success:'Wędzenie zapisane jako Zaplanowane.',
      event:JournalAnalyticsEvent.SESSION_SAVED_AS_PLANNED,
      eventProperties:{item_count:bundle.items.length,stage_count:bundle.stages.length}
    });
  }

  async function requestStartSmoking({stageId=null}={}){
    try{
      const session=editors?.sessionEditor?.snapshot?.().session;
      if(!session)throw new Error('SMOKING_SESSION_NOT_OPEN');
      const bundle=await runtime.sessionBundle(session.id);
      return prepareLiveStart({
        session,
        bundle:bundle||{},
        selectedStageId:stageId
      });
    }catch(error){
      return Object.freeze({
        ok:false,
        status:'validation_error',
        message:'Nie można rozpocząć wędzenia. Sprawdź produkty i etapy planu.',
        errorCode:code(error,'LIVE_START_INVALID')
      });
    }
  }

  async function reconcileLiveNotifications(session){
    if(!runtime.liveNotifications?.reconcileActiveSession)return null;
    try{return await runtime.liveNotifications.reconcileActiveSession({session});}
    catch{return null;}
  }

  async function startSmoking({stageId=null,confirmed=false}={}){
    const plan=await requestStartSmoking({stageId});
    if(plan.status!=='confirmation_required')return plan;
    if(!confirmed)return plan;
    const sessionId=editors?.sessionEditor?.snapshot?.().session?.id||null;
    return action(
      async()=>{
        const result=await editors.sessionEditor.startSmoking(plan.stageId);
        await reconcileLiveNotifications(result?.state?.session||editors.sessionEditor.snapshot().session);
        return result;
      },
      {
        success:'Wędzenie rozpoczęte. Aktywny jest pierwszy wybrany etap.',
        observabilityAction:'session_start',
        observabilitySessionId:sessionId
      }
    );
  }

  async function logLiveEvent(eventType,input={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_EVENT_REQUIRES_SMOKING');
    if(typeof runtime.liveQuickActions!=='function')throw new Error('LIVE_EVENT_RUNTIME_UNAVAILABLE');
    const actions=runtime.liveQuickActions(session.id);
    const method={
      wood_added:'woodAdded',
      chamber_opened:'chamberOpened',
      stage_changed:'stageChanged',
      product_checked:'productChecked',
      custom:'custom'
    }[String(eventType||'')];
    if(!method||typeof actions?.[method]!=='function')throw new TypeError('Unsupported Live event action');
    return action(
      async()=>{
        const result=await actions[method](input);
        runtime.liveAnalytics?.eventLogged?.({
          eventType:String(eventType||''),
          offline:!isOnline()
        });
        return result;
      },
      {
        success:'Zdarzenie zapisane w przebiegu wędzenia.',
        observabilityAction:'event_log',
        observabilitySessionId:session.id
      }
    );
  }

  async function logTemperature(input={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('TEMPERATURE_READING_REQUIRES_SMOKING');
    if(!runtime.temperatures?.append)throw new Error('TEMPERATURE_READING_RUNTIME_UNAVAILABLE');
    return action(
      async()=>{
        const result=await runtime.temperatures.append(session.id,input);
        runtime.liveAnalytics?.temperatureLogged?.({
          readingKind:input?.readingKind,
          sourceType:input?.sourceType,
          offline:!isOnline()
        });
        return result;
      },
      {
        success:'Temperatura zapisana.',
        observabilityAction:'temperature_log',
        observabilitySessionId:session.id
      }
    );
  }

  async function captureLiveNote({text,itemId=null}={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_NOTE_REQUIRES_SMOKING');
    if(!runtime.liveNotes?.append)throw new Error('LIVE_NOTE_RUNTIME_UNAVAILABLE');
    return action(
      ()=>runtime.liveNotes.append(session.id,{
        text,
        stageId:session.activeStageId,
        itemId:itemId||null
      }),
      {
        success:'Notatka Live zapisana.',
        observabilityAction:'note_capture',
        observabilitySessionId:session.id
      }
    );
  }

  async function captureLivePhoto(input,{file}={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_PHOTO_REQUIRES_SMOKING');
    if(!runtime.photoFlow?.attach)throw new Error('LIVE_PHOTO_RUNTIME_UNAVAILABLE');
    return action(
      ()=>runtime.photoFlow.attach({
        ...input,
        sessionId:session.id,
        stageId:session.activeStageId,
        capturedAt:input?.capturedAt||new Date().toISOString()
      },{file}),
      {
        success:'Zdjęcie Live zapisane.',
        observabilityAction:'photo_capture',
        observabilitySessionId:session.id
      }
    );
  }

  function liveStageService(){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_STAGE_TRANSITION_REQUIRES_SMOKING');
    if(typeof runtime.liveStageTransitions!=='function')throw new Error('LIVE_STAGE_TRANSITION_RUNTIME_UNAVAILABLE');
    return runtime.liveStageTransitions(session.id);
  }

  async function requestLiveStageTransition(toStageId){
    return liveStageService().plan(toStageId);
  }

  async function transitionLiveStage(toStageId,{confirmed=false}={}){
    const plan=await requestLiveStageTransition(toStageId);
    if(plan.confirmationRequired&&!confirmed)return plan;
    const sessionId=editors?.sessionEditor?.snapshot?.().session?.id||null;
    return action(
      async()=>{
        const result=await liveStageService().transition(toStageId,{confirmed});
        await reconcileLiveNotifications(result?.session||editors.sessionEditor.snapshot().session);
        const offline=!isOnline();
        if(['advance','skip'].includes(plan.reason)){
          runtime.liveAnalytics?.stageCompleted?.({reason:plan.reason,offline});
        }
        if(plan.reason==='skip'){
          runtime.liveAnalytics?.stageSkipped?.({
            skippedCount:plan.skippedStageIds?.length||0,
            offline
          });
        }
        runtime.liveAnalytics?.stageStarted?.({reason:plan.reason,offline});
        return result;
      },
      {
        success:'Etap Live został zmieniony i zapisany w historii.',
        observabilityAction:'stage_transition',
        observabilitySessionId:sessionId
      }
    );
  }

  async function insertLiveCustomStage(name){
    const sessionId=editors?.sessionEditor?.snapshot?.().session?.id||null;
    return action(
      async()=>{
        const result=await liveStageService().insertCustomStage(name);
        await reconcileLiveNotifications(result?.session||editors.sessionEditor.snapshot().session);
        runtime.liveAnalytics?.stageStarted?.({
          reason:'custom_insert',
          offline:!isOnline()
        });
        return result;
      },
      {
        success:'Własny etap został dodany i rozpoczęty.',
        observabilityAction:'custom_stage_insert',
        observabilitySessionId:sessionId
      }
    );
  }

  async function configureLiveNotifications(changes={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_NOTIFICATIONS_REQUIRE_SMOKING');
    if(!runtime.liveNotifications?.configure)throw new Error('LIVE_NOTIFICATIONS_UNAVAILABLE');
    try{
      const result=await runtime.liveNotifications.configure(changes);
      if(result?.preferences?.enabled===true){
        await reconcileLiveNotifications(session);
      }
      runtime.liveObservability?.recordAction?.({
        sessionId:session.id,
        action:'notification_configure',
        outcome:'success'
      });
      const notice={
        enabled:'Przypomnienia Live są włączone na tym urządzeniu.',
        configured:'Ustawienia przypomnień Live zostały zapisane na tym urządzeniu.',
        disabled:'Przypomnienia Live są wyłączone na tym urządzeniu.',
        permission_denied:'System nie zezwolił na powiadomienia. Możesz zmienić zgodę w ustawieniach urządzenia.',
        unsupported:'Lokalne powiadomienia nie są dostępne na tym urządzeniu.'
      }[result?.status]||'Ustawienia przypomnień Live zostały zapisane.';
      const kind=['permission_denied','unsupported'].includes(result?.status)?'warning':'success';
      return buildEditorState({notice,noticeKind:kind});
    }catch(error){
      runtime.liveObservability?.recordAction?.({
        sessionId:session.id,
        action:'notification_configure',
        outcome:'failure',
        errorCode:code(error,'LIVE_NOTIFICATIONS_FAILED')
      });
      return buildEditorState({
        notice:'Nie udało się zapisać przypomnień Live. Wędzenie działa dalej bez zmian.',
        noticeKind:'warning'
      });
    }
  }

  async function showEarlierLiveTimeline(){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session||session.status!=='smoking')throw new Error('LIVE_TIMELINE_REQUIRES_SMOKING');
    liveTimelineLimit=Math.min(1000,liveTimelineLimit+60);
    return buildEditorState();
  }

  async function acceptRemoteLiveSessionState(sessionId){
    const id=String(sessionId||'').trim();
    if(!id)throw new TypeError('sessionId is required');
    if(!isOnline()){
      return buildEditorState({
        notice:'Rozwiązanie konfliktu wymaga połączenia z serwerem.',
        noticeKind:'warning'
      });
    }
    if(!runtime.liveConflicts?.acceptRemoteSession){
      return buildEditorState({
        notice:'Odzyskanie stanu z serwera jest chwilowo niedostępne.',
        noticeKind:'warning'
      });
    }
    try{
      await runtime.liveConflicts.acceptRemoteSession(id);
      if(editors?.sessionEditor?.snapshot?.().session?.id===id){
        await editors.sessionEditor.open(id);
      }
      await reconcileLiveNotifications(editors?.sessionEditor?.snapshot?.().session);
      return buildEditorState({
        notice:'Przyjęto aktualny stan sesji z serwera. Niezależne lokalne odczyty, notatki i zdjęcia pozostały zachowane.',
        noticeKind:'success'
      });
    }catch(error){
      return buildEditorState({
        notice:'Nie udało się przyjąć stanu sesji z serwera. Kod: '+code(error,'LIVE_CONFLICT_RECOVERY_FAILED'),
        noticeKind:'error'
      });
    }
  }

  async function discardRejectedLiveStageEvidence(sessionId){
    const id=String(sessionId||'').trim();
    if(!id)throw new TypeError('sessionId is required');
    if(!runtime.liveConflicts?.discardRejectedStageEvidence){
      return buildEditorState({
        notice:'Odrzucenie przegranej zmiany etapu jest chwilowo niedostępne.',
        noticeKind:'warning'
      });
    }
    try{
      const result=await runtime.liveConflicts.discardRejectedStageEvidence(id);
      return buildEditorState({
        notice:result.discarded.length
          ?'Odrzucono przegraną lokalną zmianę etapu. Możesz wykonać nową zmianę od aktualnego stanu.'
          :'Brak odrzuconych zmian etapu do usunięcia.',
        noticeKind:'success'
      });
    }catch(error){
      return buildEditorState({
        notice:'Nie udało się odrzucić lokalnej zmiany etapu. Kod: '+code(error,'LIVE_CONFLICT_DISCARD_FAILED'),
        noticeKind:'error'
      });
    }
  }

  async function finishSession({confirmed=false}={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(!confirmed)return editors.sessionEditor.requestFinish();
    return action(async()=>{
      const result=await editors.sessionEditor.finish({confirmed:true});
      try{await runtime.liveNotifications?.clear?.();}catch{}
      return result;
    },{
      success:'Wędzenie zakończone. Dodaj ocenę wyniku i wnioski.',
      observabilityAction:'session_complete',
      observabilitySessionId:session.id
    });
  }

  async function saveOutcome(input={}){
    const session=editors?.sessionEditor?.snapshot?.().session;
    if(!session)throw new Error('SMOKING_SESSION_NOT_OPEN');
    return action(
      ()=>runtime.outcomes.save(session,input),
      {success:'Ocena i wnioski zostały zapisane lokalnie.'}
    );
  }

  async function cancelSession({confirmed=false}={}){
    if(!confirmed)return editors.sessionEditor.requestCancel();
    return action(()=>editors.sessionEditor.cancel({confirmed:true}),{
      success:'Wędzenie anulowane.',
      event:JournalAnalyticsEvent.SESSION_EDIT,
      eventProperties:{field:'cancel'}
    });
  }

  async function deleteSession({confirmed=false}={}){
    if(!confirmed)return editors.sessionEditor.requestDelete();
    try{
      await editors.sessionEditor.remove({confirmed:true});
      track(JournalAnalyticsEvent.SESSION_DELETED,{offline:!isOnline()});
      editors=null;
      refs=null;
      return listState({notice:'Plan został usunięty.',noticeKind:'success'});
    }catch(error){
      return buildEditorState({notice:'Nie udało się usunąć planu. Kod: '+code(error),noticeKind:'error'});
    }
  }

  async function syncPending(){
    try{
      const result=await runtime.syncPending();
      if(state.kind==='editor')return buildEditorState({
        notice:result?.ok&&result.processed?'Synchronizacja zakończona.':'Brak zmian do synchronizacji.',
        noticeKind:'success'
      });
      return listState();
    }catch(error){
      if(state.kind==='editor')return buildEditorState({notice:'Synchronizacja nie powiodła się. Dane lokalne pozostają zapisane.',noticeKind:'warning'});
      return listState();
    }
  }

  function getState(){return state;}
  function render(){return journalPageMarkup(state);}

  return Object.freeze({
    refresh,recoverActiveSession,open,createDraft,resumeDraft,getState,render,
    saveTitle,saveSchedule,setEquipment,createCustomProduct,
    beginItemEdit,cancelItemEdit,saveItem,moveItem,removeItem,savePreparation,
    beginWoodEdit,cancelWoodEdit,saveWood,moveWood,removeWood,
    beginStageEdit,cancelStageEdit,saveStage,moveStage,removeStage,
    saveSessionNote,clearSessionNote,attachPhoto,removePhoto,
    saveAsPlanned,requestStartSmoking,startSmoking,logLiveEvent,logTemperature,captureLiveNote,captureLivePhoto,
    requestLiveStageTransition,transitionLiveStage,insertLiveCustomStage,configureLiveNotifications,showEarlierLiveTimeline,
    acceptRemoteLiveSessionState,discardRejectedLiveStageEvidence,
    finishSession,saveOutcome,cancelSession,deleteSession,syncPending
  });
}
